import React, { useState, useEffect, useCallback, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Box, Grid, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import {
  BusinessTemplateType,
  CustomSettings,
  getDefaultSectionOrderForTemplateType,
  isInvoiceOrProformaTemplateType,
  isDefaultBusinessTemplate,
  isProformaInvoiceTemplateType,
  migrateProformaSettingsFromLegacy,
  withDefaultProformaHeaderDetails,
  withDesignVariantInSettings,
} from "subModule/src/services/invoice/types";
import { InvoiceProfileService } from "subModule/src/core/services";
import GeneralSettingsPanel from "./panels/GeneralSettingsPanel";
import LogoSettingsPanel from "./panels/LogoSettingsPanel";
import HeaderSettingsPanel from "./panels/HeaderSettingsPanel";
import VehicleInfoSettingsPanel from "./panels/VehicleInfoSettingsPanel";
import TableSettingsPanel from "./panels/TableSettingsPanel";
import SummaryFooterSettingsPanel from "./panels/SummaryFooterSettingsPanel";
import InspectionSettingsPanel from "./panels/InspectionSettingsPanel";
import InvoicePreview from "./InvoicePreview";
import LayoutCustomizer from "./LayoutCustomizer";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { usePreFillLabels } from "./hooks/usePreFillLabels";
import { normalizeThermalSectionOrder } from "./thermal/thermalInvoiceUtils";

interface InvoiceTemplateCustomizerProps {
  templateId?: number;
  initialSettings?: CustomSettings;
  onSave?: (settings: CustomSettings) => void;
  onCancel?: () => void;
  formFields: any;
  params: any;
  IsBtnEnabled: boolean;
  isViewMode?: boolean; // Add this prop for view mode
  onSettingsChange?: (settings: CustomSettings) => void; // Callback to sync settings to parent
  getCurrentSettings?: (getter: () => CustomSettings) => void; // Ref callback to get current settings
  getApplySettingsChange?: (
    applier: (newSettings: Partial<CustomSettings>) => void,
  ) => void;
  formData?: any;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4, INSPECTION = 5, DELIVERY_NOTE = 6, PROFORMA_INVOICE = 7
  isDefault?: boolean;
}

// Helper function to get default settings based on template type
const getDefaultSettings = (templateType: number = 2): CustomSettings => {
  const baseSettings: CustomSettings = {
    designVariant: "classic",
    languageDisplay: "both",
    dateFormat: "DD-MM-YYYY",
    showLogo: true,
    showHeader: true,
    showVehicleInfo: true,
    showTerms: true,
    showNotes: true,
    invoiceType: "normal",
    logoPosition: "left",
    logoWidth: 150,
    logoHeight: 80,
    useDefaultLogoSize: true,
    logoFitToWidth: false,
    showQR: false,
    showCompanySection: true,
    showCustomerSection: true,
    invoiceHeadingPrimary: "INVOICE",
    invoiceHeadingSecondary: "",
    showInvoiceDirection: "column",
    companyFields: {
      name: true,
      phone: true,
      email: true,
      address: true,
      vat: false,
    },
    customerFields: {
      name: true,
      phone: true,
      email: false,
      address: false,
      vat: false,
    },
    vehicleFields: {
      color: false,
      fuelType: true,
      vin: false,
      mileage: true,
      make: false,
      model: false,
      year: false,
      plateNumber: true,
    },
    showFooter: true,
    footerText: "This is system generated invoice/JC/WO/Estimation.",
    footerItems: {
      address: true,
      email: true,
      phone: true,
      socialLinks: false,
      pageNumber: true,
    },
  };

  // Inspection template (type 5) - different defaults
  if (templateType === 5) {
    return {
      ...baseSettings,
      showTable: false,
      showPaymentInfo: false,
      showInspectionItems: true,
      showVehicleImage: true,
      vehicleImagePosition: "right",
      showInspectionRemarks: true,
      inspectionRemarksLabel: {
        primary: "Remarks",
        secondary: "ملاحظات",
      },
      showTechnicians: true,
      inspectionTechniciansLabel: {
        primary: "Technicians",
        secondary: "الفنيين",
      },
      showCustomerSignature: true,
      customerSignatureLabel: {
        primary: "Customer Signature",
        secondary: "توقيع العميل",
      },
      inspectionItems: [
        { id: "1", partName: "Part 1", status: "working", notes: "" },
        { id: "2", partName: "Part 2", status: "working", notes: "" },
        { id: "3", partName: "Part 3", status: "working", notes: "" },
      ],
      sectionOrder: getDefaultSectionOrderForTemplateType(
        BusinessTemplateType.INSPECTION,
      ),
    } as any;
  }

  // Type-specific defaults
  const typeSpecificDefaults: Partial<CustomSettings> = {};

  if (templateType === BusinessTemplateType.INVOICE) {
    // INVOICE
    typeSpecificDefaults.designVariant = "modern";
    typeSpecificDefaults.printFormat = "normal";
    typeSpecificDefaults.thermalPaperWidthPreset = "80";
    typeSpecificDefaults.thermalCustomWidthMm = 80;
    typeSpecificDefaults.showInvoiceNumber = true;
    typeSpecificDefaults.invoiceNumberPosition = "left";
    typeSpecificDefaults.invoiceNumberFontSize = 18;
    typeSpecificDefaults.invoiceNumberFontWeight = 700;
    typeSpecificDefaults.invoiceType = "normal";
    typeSpecificDefaults.companyFields = {
      ...baseSettings.companyFields,
      vat: true,
    };
    typeSpecificDefaults.vehicleFields = {
      ...baseSettings.vehicleFields,
      plateNumber: true,
      mileage: true,
    };
  } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
    // PROFORMA INVOICE
    typeSpecificDefaults.printFormat = "normal";
    typeSpecificDefaults.thermalPaperWidthPreset = "80";
    typeSpecificDefaults.thermalCustomWidthMm = 80;
    typeSpecificDefaults.showProformaInvoiceNumber = true;
    typeSpecificDefaults.proformaInvoiceNumberPosition = "left";
    typeSpecificDefaults.proformaInvoiceNumberFontSize = 18;
    typeSpecificDefaults.proformaInvoiceNumberFontWeight = 700;
    typeSpecificDefaults.proformaInvoiceHeadingPrimary = "PROFORMA INVOICE";
    typeSpecificDefaults.proformaInvoiceHeadingSecondary = "";
    typeSpecificDefaults.invoiceType = "normal";
  } else if (templateType === 3) {
    // JOBCARD
    typeSpecificDefaults.showJobcardNumber = true;
    typeSpecificDefaults.jobcardNumberPosition = "left";
    typeSpecificDefaults.jobcardNumberFontSize = 18;
    typeSpecificDefaults.jobcardNumberFontWeight = 700;
    typeSpecificDefaults.jobcardHeadingPrimary = "JOBCARD";
    typeSpecificDefaults.jobcardHeadingSecondary = "";
    typeSpecificDefaults.invoiceType = "normal";
  } else if (templateType === 4) {
    // WORKORDER
    typeSpecificDefaults.showWorkorderNumber = true;
    typeSpecificDefaults.workorderNumberPosition = "left";
    typeSpecificDefaults.workorderNumberFontSize = 18;
    typeSpecificDefaults.workorderNumberFontWeight = 700;
    typeSpecificDefaults.workorderHeadingPrimary = "WORKORDER";
    typeSpecificDefaults.workorderHeadingSecondary = "";
    typeSpecificDefaults.invoiceType = "normal";
  } else if (templateType === 6) {
    // DELIVERY NOTE
    typeSpecificDefaults.showDeliveryNoteNumber = true;
    typeSpecificDefaults.deliveryNoteNumberPosition = "left";
    typeSpecificDefaults.deliveryNoteNumberFontSize = 18;
    typeSpecificDefaults.deliveryNoteNumberFontWeight = 700;
    typeSpecificDefaults.deliveryNoteHeadingPrimary = "DELIVERY NOTE";
    typeSpecificDefaults.deliveryNoteHeadingSecondary = "";
    typeSpecificDefaults.invoiceType = "normal";
  }

  const isProforma = isProformaInvoiceTemplateType(templateType);

  // Default for INVOICE, PROFORMA, JOBCARD, WORKORDER, DELIVERY NOTE
  return {
    ...baseSettings,
    ...typeSpecificDefaults,
    ...(isProforma && {
      showVehicleInfo: false,
      showPaymentInfo: false,
    }),
    showTable: true,
    showPaymentInfo: isProforma ? false : true,
    tableLayout: "combined",
    showPackages: true,
    showServices: true,
    showItems: true,
    descriptionDisplay: "all",
    itemDisplayOptions: {
      showTitle: true,
      showCode: true,
      showDescription: false,
    },
    tableColumns: {
      serialNo: true,
      item: true,
      quantity: true,
      unitPrice: true,
      amount: true,
      discount: true,
      discountedSubtotal: true,
      tax: true,
      totalWithTax: true,
    },
    consolidatedTableColumns: {
      serialNo: true,
      vehicleName: true,
      vehiclePlateNo: true,
      deliveryNoteNo: true,
      subtotal: true,
      discount: true,
      discountedSubtotal: true,
      tax: true,
      total: true,
    },
    summaryItems: {
      subtotal: true,
      discount: true,
      discountedSubtotal: true,
      tax: true,
      total: true,
    },
    sectionOrder: getDefaultSectionOrderForTemplateType(templateType),
  };
};

// const defaultSettings: CustomSettings = getDefaultSettings(2);

const InvoiceTemplateCustomizer: React.FC<InvoiceTemplateCustomizerProps> = ({
  templateId,
  initialSettings,
  onSave,
  onCancel,
  formFields,
  params,
  IsBtnEnabled,
  isViewMode = false, // Default to false
  onSettingsChange,
  getCurrentSettings,
  getApplySettingsChange,
  formData,
  templateType = 2, // Default to INVOICE
  isDefault = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const { preFillLabels } = usePreFillLabels();

  const [settings, setSettings] = useState<CustomSettings>(() => {
    const defaultsForType = getDefaultSettings(templateType);

    const initial = initialSettings
      ? (() => {
          // Merge initial settings with defaults to ensure all fields are present
          const merged = {
            ...defaultsForType,
            ...initialSettings,
            // Ensure inspection-specific settings are preserved or defaulted
            ...(templateType === 5 && {
              showInspectionItems: initialSettings?.showInspectionItems ?? true,
              showVehicleImage: initialSettings?.showVehicleImage ?? true,
              showInspectionRemarks:
                initialSettings?.showInspectionRemarks ?? true,
              showTechnicians: initialSettings?.showTechnicians ?? true,
              showCustomerSignature:
                initialSettings?.showCustomerSignature ?? true,
              inspectionItems:
                initialSettings?.inspectionItems ||
                defaultsForType.inspectionItems,
              vehicleImagePosition:
                initialSettings?.vehicleImagePosition ||
                defaultsForType.vehicleImagePosition,
              inspectionRemarksLabel:
                initialSettings?.inspectionRemarksLabel ||
                defaultsForType.inspectionRemarksLabel,
              inspectionTechniciansLabel:
                initialSettings?.inspectionTechniciansLabel ||
                defaultsForType.inspectionTechniciansLabel,
              customerSignatureLabel:
                initialSettings?.customerSignatureLabel ||
                defaultsForType.customerSignatureLabel,
            }),
          };
          return preFillLabels(
            withDefaultProformaHeaderDetails(merged, templateType, isDefault),
          );
        })()
      : preFillLabels(
          withDefaultProformaHeaderDetails(
            defaultsForType,
            templateType,
            isDefault,
          ),
        );

    // Remove descriptionDisplay if itemDisplayOptions exists
    if (initial.itemDisplayOptions) {
      const rest = { ...initial };
      delete rest.descriptionDisplay;
      return rest;
    }

    return initial;
  });
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);
  const templateIdRef = useRef(templateId);
  const templateTypeRef = useRef(Number(templateType));
  const settingsRef = useRef<CustomSettings>(settings); // Ref to always have latest settings

  // Update ref whenever settings change
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Initialize settings only once on mount or when templateId changes
  useEffect(() => {
    // Only initialize if templateId actually changed or we haven't initialized yet
    if (templateId !== templateIdRef.current || !initializedRef.current) {
      const previousTemplateId = templateIdRef.current;
      templateIdRef.current = templateId;

      if (!initializedRef.current) {
        if (initialSettings) {
          // Get default settings for the template type
          const defaultsForType = getDefaultSettings(templateType);

          // Merge initial settings with defaults to ensure all fields are present
          const mergedSettings = {
            ...defaultsForType,
            ...initialSettings,
            // Ensure inspection-specific settings are preserved or defaulted
            ...(templateType === 5 && {
              showInspectionItems: initialSettings?.showInspectionItems ?? true,
              showVehicleImage: initialSettings?.showVehicleImage ?? true,
              showInspectionRemarks:
                initialSettings?.showInspectionRemarks ?? true,
              showTechnicians: initialSettings?.showTechnicians ?? true,
              showCustomerSignature:
                initialSettings?.showCustomerSignature ?? true,
              inspectionItems:
                initialSettings?.inspectionItems ||
                defaultsForType.inspectionItems,
              vehicleImagePosition:
                initialSettings?.vehicleImagePosition ||
                defaultsForType.vehicleImagePosition,
              inspectionRemarksLabel:
                initialSettings?.inspectionRemarksLabel ||
                defaultsForType.inspectionRemarksLabel,
              inspectionTechniciansLabel:
                initialSettings?.inspectionTechniciansLabel ||
                defaultsForType.inspectionTechniciansLabel,
              customerSignatureLabel:
                initialSettings?.customerSignatureLabel ||
                defaultsForType.customerSignatureLabel,
            }),
          };

          const loadedSettings = preFillLabels(
            withDefaultProformaHeaderDetails(
              mergedSettings,
              templateType,
              isDefault,
            ),
          );
          // Remove descriptionDisplay if itemDisplayOptions exists
          if (loadedSettings.itemDisplayOptions) {
            const rest = { ...loadedSettings };
            delete rest.descriptionDisplay;
            setSettings(rest);
          } else {
            setSettings(loadedSettings);
          }
          initializedRef.current = true;
        } else if (templateId) {
          loadTemplate();
        } else {
          initializedRef.current = true;
        }
      } else if (templateId !== previousTemplateId && templateId) {
        // Template ID changed, reload
        initializedRef.current = false;
        loadTemplate();
      }
    }
  }, [templateId]); // Only depend on templateId

  // Watch for templateType changes and update settings accordingly
  useEffect(() => {
    // Only update if templateType actually changed and component is initialized
    const normalizedType = Number(templateType);
    if (initializedRef.current && templateTypeRef.current !== normalizedType) {
      const previousType = templateTypeRef.current;
      templateTypeRef.current = normalizedType;
      const defaultsForType = getDefaultSettings(normalizedType);

      setSettings((prev) => {
        // Merge current settings with new type defaults
        const merged = {
          ...defaultsForType,
          ...prev,
          // Set type-specific number positions
          ...(normalizedType === BusinessTemplateType.INVOICE && {
            invoiceNumberPosition:
              prev?.invoiceNumberPosition ||
              defaultsForType.invoiceNumberPosition ||
              "left",
            showInvoiceNumber: prev?.showInvoiceNumber ?? true,
            invoiceNumberFontSize:
              prev?.invoiceNumberFontSize ||
              defaultsForType.invoiceNumberFontSize ||
              18,
            invoiceNumberFontWeight:
              prev?.invoiceNumberFontWeight ||
              defaultsForType.invoiceNumberFontWeight ||
              700,
          }),
          ...(normalizedType === BusinessTemplateType.PROFORMA_INVOICE && {
            proformaInvoiceNumberPosition:
              prev?.proformaInvoiceNumberPosition ||
              defaultsForType.proformaInvoiceNumberPosition ||
              "left",
            showProformaInvoiceNumber: prev?.showProformaInvoiceNumber ?? true,
            proformaInvoiceNumberFontSize:
              prev?.proformaInvoiceNumberFontSize ||
              defaultsForType.proformaInvoiceNumberFontSize ||
              18,
            proformaInvoiceNumberFontWeight:
              prev?.proformaInvoiceNumberFontWeight ||
              defaultsForType.proformaInvoiceNumberFontWeight ||
              700,
            proformaInvoiceHeadingPrimary:
              prev?.proformaInvoiceHeadingPrimary ||
              defaultsForType.proformaInvoiceHeadingPrimary ||
              "PROFORMA INVOICE",
            proformaInvoiceHeadingSecondary:
              prev?.proformaInvoiceHeadingSecondary ??
              defaultsForType.proformaInvoiceHeadingSecondary ??
              "",
            printFormat:
              prev?.printFormat || defaultsForType.printFormat || "normal",
            thermalPaperWidthPreset:
              prev?.thermalPaperWidthPreset ||
              defaultsForType.thermalPaperWidthPreset ||
              "80",
            thermalCustomWidthMm:
              prev?.thermalCustomWidthMm ??
              defaultsForType.thermalCustomWidthMm ??
              80,
            showVehicleInfo: false,
            showPaymentInfo: false,
          }),
          ...(previousType === BusinessTemplateType.PROFORMA_INVOICE &&
            !isProformaInvoiceTemplateType(normalizedType) && {
              showCompanySection: defaultsForType.showCompanySection ?? true,
              showVehicleInfo: defaultsForType.showVehicleInfo ?? true,
              showPaymentInfo: defaultsForType.showPaymentInfo ?? true,
              companyFields: defaultsForType.companyFields,
            }),
          ...(normalizedType === 3 && {
            jobcardNumberPosition:
              prev?.jobcardNumberPosition ||
              defaultsForType.jobcardNumberPosition ||
              "left",
            showJobcardNumber: prev?.showJobcardNumber ?? true,
            jobcardNumberFontSize:
              prev?.jobcardNumberFontSize ||
              defaultsForType.jobcardNumberFontSize ||
              18,
            jobcardNumberFontWeight:
              prev?.jobcardNumberFontWeight ||
              defaultsForType.jobcardNumberFontWeight ||
              700,
            jobcardHeadingPrimary:
              prev?.jobcardHeadingPrimary ||
              defaultsForType.jobcardHeadingPrimary ||
              "JOBCARD",
            jobcardHeadingSecondary:
              prev?.jobcardHeadingSecondary ||
              defaultsForType.jobcardHeadingSecondary ||
              "",
          }),
          ...(normalizedType === 4 && {
            workorderNumberPosition:
              prev?.workorderNumberPosition ||
              defaultsForType.workorderNumberPosition ||
              "left",
            showWorkorderNumber: prev?.showWorkorderNumber ?? true,
            workorderNumberFontSize:
              prev?.workorderNumberFontSize ||
              defaultsForType.workorderNumberFontSize ||
              18,
            workorderNumberFontWeight:
              prev?.workorderNumberFontWeight ||
              defaultsForType.workorderNumberFontWeight ||
              700,
            workorderHeadingPrimary:
              prev?.workorderHeadingPrimary ||
              defaultsForType.workorderHeadingPrimary ||
              "WORKORDER",
            workorderHeadingSecondary:
              prev?.workorderHeadingSecondary ||
              defaultsForType.workorderHeadingSecondary ||
              "",
          }),
          ...(normalizedType === 6 && {
            deliveryNoteNumberPosition:
              prev?.deliveryNoteNumberPosition ||
              defaultsForType.deliveryNoteNumberPosition ||
              "left",
            showDeliveryNoteNumber: prev?.showDeliveryNoteNumber ?? true,
            deliveryNoteNumberFontSize:
              prev?.deliveryNoteNumberFontSize ||
              defaultsForType.deliveryNoteNumberFontSize ||
              18,
            deliveryNoteNumberFontWeight:
              prev?.deliveryNoteNumberFontWeight ||
              defaultsForType.deliveryNoteNumberFontWeight ||
              700,
            deliveryNoteHeadingPrimary:
              prev?.deliveryNoteHeadingPrimary ||
              defaultsForType.deliveryNoteHeadingPrimary ||
              "DELIVERY NOTE",
            deliveryNoteHeadingSecondary:
              prev?.deliveryNoteHeadingSecondary ||
              defaultsForType.deliveryNoteHeadingSecondary ||
              "",
          }),
          // Preserve user customizations but ensure type-specific defaults
          ...(normalizedType === 5 && {
            showInspectionItems: prev?.showInspectionItems ?? true,
            showVehicleImage: prev?.showVehicleImage ?? true,
            showInspectionRemarks: prev?.showInspectionRemarks ?? true,
            showTechnicians: prev?.showTechnicians ?? true,
            showCustomerSignature: prev?.showCustomerSignature ?? true,
            showVehicleInfo:
              previousType === BusinessTemplateType.PROFORMA_INVOICE
                ? true
                : (prev?.showVehicleInfo ??
                  defaultsForType.showVehicleInfo ??
                  true),
            showCompanySection:
              previousType === BusinessTemplateType.PROFORMA_INVOICE
                ? (defaultsForType.showCompanySection ?? true)
                : (prev?.showCompanySection ??
                  defaultsForType.showCompanySection ??
                  true),
            inspectionItems:
              prev?.inspectionItems || defaultsForType.inspectionItems,
            vehicleImagePosition:
              prev?.vehicleImagePosition ||
              defaultsForType.vehicleImagePosition,
            inspectionRemarksLabel:
              prev?.inspectionRemarksLabel ||
              defaultsForType.inspectionRemarksLabel,
            inspectionTechniciansLabel:
              prev?.inspectionTechniciansLabel ||
              defaultsForType.inspectionTechniciansLabel,
            customerSignatureLabel:
              prev?.customerSignatureLabel ||
              defaultsForType.customerSignatureLabel,
            showTable: false,
            showPaymentInfo: false,
          }),
          ...(normalizedType !== 5 && {
            showTable: previousType === 5 ? true : (prev?.showTable ?? true),
            showPaymentInfo:
              previousType === 5
                ? true
                : previousType === BusinessTemplateType.PROFORMA_INVOICE
                  ? (defaultsForType.showPaymentInfo ?? true)
                  : (prev?.showPaymentInfo ?? true),
          }),
          // Consolidated Invoice is invoice-only; clear on every type change
          invoiceType: "normal",
          // Modern design is invoice-only; default to modern for invoices
          designVariant:
            normalizedType === BusinessTemplateType.INVOICE
              ? previousType === BusinessTemplateType.INVOICE
                ? prev?.designVariant || "modern"
                : "modern"
              : "classic",
          sectionOrder: getDefaultSectionOrderForTemplateType(normalizedType),
        };

        const enriched = preFillLabels(merged);
        settingsRef.current = enriched;
        if (onSettingsChange) {
          onSettingsChange(enriched);
        }
        return enriched;
      });
    } else if (!initializedRef.current) {
      templateTypeRef.current = Number(templateType);
    }
  }, [templateType, onSettingsChange, preFillLabels]);

  const loadTemplate = async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const response =
        await InvoiceProfileService.getInvoiceTemplateByID(templateId);
      if (response.success && response.data) {
        // Get template type from response or use prop
        const loadedTemplateType = response.data.type || templateType;
        const isDefaultTemplate =
          isDefault || isDefaultBusinessTemplate(response.data);
        const templateSettings = withDesignVariantInSettings(
          (response.data.settings ||
            response.data.attributes) as CustomSettings,
          response.data.designVariant,
        );

        // Get default settings for the template type
        const defaultsForType = getDefaultSettings(loadedTemplateType);

        // Merge loaded settings with defaults to ensure all fields are present
        const mergedSettings = {
          ...defaultsForType,
          ...templateSettings,
          // Set type-specific number positions based on loaded template type
          ...(loadedTemplateType === BusinessTemplateType.INVOICE && {
            invoiceNumberPosition:
              templateSettings?.invoiceNumberPosition ||
              defaultsForType.invoiceNumberPosition ||
              "left",
            showInvoiceNumber: templateSettings?.showInvoiceNumber ?? true,
            invoiceNumberFontSize:
              templateSettings?.invoiceNumberFontSize ||
              defaultsForType.invoiceNumberFontSize ||
              18,
            invoiceNumberFontWeight:
              templateSettings?.invoiceNumberFontWeight ||
              defaultsForType.invoiceNumberFontWeight ||
              700,
          }),
          ...(loadedTemplateType === BusinessTemplateType.PROFORMA_INVOICE && {
            ...migrateProformaSettingsFromLegacy(templateSettings || {}),
            proformaInvoiceNumberPosition:
              templateSettings?.proformaInvoiceNumberPosition ||
              templateSettings?.invoiceNumberPosition ||
              defaultsForType.proformaInvoiceNumberPosition ||
              "left",
            showProformaInvoiceNumber:
              templateSettings?.showProformaInvoiceNumber ??
              templateSettings?.showInvoiceNumber ??
              true,
            proformaInvoiceNumberFontSize:
              templateSettings?.proformaInvoiceNumberFontSize ||
              templateSettings?.invoiceNumberFontSize ||
              defaultsForType.proformaInvoiceNumberFontSize ||
              18,
            proformaInvoiceNumberFontWeight:
              templateSettings?.proformaInvoiceNumberFontWeight ||
              templateSettings?.invoiceNumberFontWeight ||
              defaultsForType.proformaInvoiceNumberFontWeight ||
              700,
            proformaInvoiceHeadingPrimary:
              templateSettings?.proformaInvoiceHeadingPrimary ||
              templateSettings?.invoiceHeadingPrimary ||
              defaultsForType.proformaInvoiceHeadingPrimary ||
              "PROFORMA INVOICE",
            proformaInvoiceHeadingSecondary:
              templateSettings?.proformaInvoiceHeadingSecondary ??
              templateSettings?.invoiceHeadingSecondary ??
              defaultsForType.proformaInvoiceHeadingSecondary ??
              "",
            showCompanySection:
              templateSettings?.showCompanySection ??
              defaultsForType.showCompanySection ??
              true,
            showVehicleInfo: false,
            showPaymentInfo: false,
          }),
          ...(loadedTemplateType === 3 && {
            jobcardNumberPosition:
              templateSettings?.jobcardNumberPosition ||
              defaultsForType.jobcardNumberPosition ||
              "left",
            showJobcardNumber: templateSettings?.showJobcardNumber ?? true,
            jobcardNumberFontSize:
              templateSettings?.jobcardNumberFontSize ||
              defaultsForType.jobcardNumberFontSize ||
              18,
            jobcardNumberFontWeight:
              templateSettings?.jobcardNumberFontWeight ||
              defaultsForType.jobcardNumberFontWeight ||
              700,
            jobcardHeadingPrimary:
              templateSettings?.jobcardHeadingPrimary ||
              defaultsForType.jobcardHeadingPrimary ||
              "JOBCARD",
            jobcardHeadingSecondary:
              templateSettings?.jobcardHeadingSecondary ||
              defaultsForType.jobcardHeadingSecondary ||
              "",
          }),
          ...(loadedTemplateType === 4 && {
            workorderNumberPosition:
              templateSettings?.workorderNumberPosition ||
              defaultsForType.workorderNumberPosition ||
              "left",
            showWorkorderNumber: templateSettings?.showWorkorderNumber ?? true,
            workorderNumberFontSize:
              templateSettings?.workorderNumberFontSize ||
              defaultsForType.workorderNumberFontSize ||
              18,
            workorderNumberFontWeight:
              templateSettings?.workorderNumberFontWeight ||
              defaultsForType.workorderNumberFontWeight ||
              700,
            workorderHeadingPrimary:
              templateSettings?.workorderHeadingPrimary ||
              defaultsForType.workorderHeadingPrimary ||
              "WORKORDER",
            workorderHeadingSecondary:
              templateSettings?.workorderHeadingSecondary ||
              defaultsForType.workorderHeadingSecondary ||
              "",
          }),
          ...(loadedTemplateType === 6 && {
            deliveryNoteNumberPosition:
              templateSettings?.deliveryNoteNumberPosition ||
              defaultsForType.deliveryNoteNumberPosition ||
              "left",
            showDeliveryNoteNumber:
              templateSettings?.showDeliveryNoteNumber ?? true,
            deliveryNoteNumberFontSize:
              templateSettings?.deliveryNoteNumberFontSize ||
              defaultsForType.deliveryNoteNumberFontSize ||
              18,
            deliveryNoteNumberFontWeight:
              templateSettings?.deliveryNoteNumberFontWeight ||
              defaultsForType.deliveryNoteNumberFontWeight ||
              700,
            deliveryNoteHeadingPrimary:
              templateSettings?.deliveryNoteHeadingPrimary ||
              defaultsForType.deliveryNoteHeadingPrimary ||
              "DELIVERY NOTE",
            deliveryNoteHeadingSecondary:
              templateSettings?.deliveryNoteHeadingSecondary ||
              defaultsForType.deliveryNoteHeadingSecondary ||
              "",
          }),
          // Ensure inspection-specific settings are preserved or defaulted
          ...(loadedTemplateType === 5 && {
            showInspectionItems: templateSettings?.showInspectionItems ?? true,
            showVehicleImage: templateSettings?.showVehicleImage ?? true,
            showInspectionRemarks:
              templateSettings?.showInspectionRemarks ?? true,
            showTechnicians: templateSettings?.showTechnicians ?? true,
            showCustomerSignature:
              templateSettings?.showCustomerSignature ?? true,
            inspectionItems:
              templateSettings?.inspectionItems ||
              defaultsForType.inspectionItems,
            vehicleImagePosition:
              templateSettings?.vehicleImagePosition ||
              defaultsForType.vehicleImagePosition,
            inspectionRemarksLabel:
              templateSettings?.inspectionRemarksLabel ||
              defaultsForType.inspectionRemarksLabel,
            inspectionTechniciansLabel:
              templateSettings?.inspectionTechniciansLabel ||
              defaultsForType.inspectionTechniciansLabel,
            customerSignatureLabel:
              templateSettings?.customerSignatureLabel ||
              defaultsForType.customerSignatureLabel,
            sectionOrder: templateSettings?.sectionOrder?.length
              ? templateSettings.sectionOrder
              : getDefaultSectionOrderForTemplateType(loadedTemplateType),
            showTable: false,
            showPaymentInfo: false,
          }),
          ...(isInvoiceOrProformaTemplateType(loadedTemplateType) &&
            (templateSettings as CustomSettings)?.printFormat === "thermal" && {
              sectionOrder: normalizeThermalSectionOrder(
                templateSettings?.sectionOrder,
              ),
              showVehicleInfo: false,
              showTerms: false,
              ...(isProformaInvoiceTemplateType(loadedTemplateType) && {
                showPaymentInfo: false,
              }),
            }),
        };

        const enrichedSettings = preFillLabels(
          withDefaultProformaHeaderDetails(
            mergedSettings,
            loadedTemplateType,
            isDefaultTemplate,
          ),
        );
        // Remove descriptionDisplay if itemDisplayOptions exists
        if (enrichedSettings.itemDisplayOptions) {
          const rest = { ...enrichedSettings };
          delete rest.descriptionDisplay;
          setSettings(rest);
          settingsRef.current = rest;
        } else {
          setSettings(enrichedSettings);
          settingsRef.current = enrichedSettings;
        }
        initializedRef.current = true;
      }
    } catch (error: any) {
      enqueueSnackbar(
        <AlertMessages text={error?.message || "Failed to load template"} />,
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = useCallback(
    (newSettings: Partial<CustomSettings>) => {
      setSettings((prev) => {
        const updated = { ...prev, ...newSettings };

        // If itemDisplayOptions is being set, remove descriptionDisplay to avoid conflicts
        if (newSettings.itemDisplayOptions !== undefined) {
          delete updated.descriptionDisplay;
        }

        const enriched = preFillLabels(updated);
        // Update ref with latest settings
        settingsRef.current = enriched;
        // Sync to parent component in real-time
        if (onSettingsChange) {
          onSettingsChange(enriched);
        }
        return enriched;
      });
    },
    [preFillLabels, onSettingsChange],
  );

  // Update ref whenever settings change
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Expose getter function to parent via ref callback
  // The getter function will always return the current settings from ref
  useEffect(() => {
    if (getCurrentSettings) {
      getCurrentSettings(() => {
        // Always return the latest settings from ref
        return preFillLabels(settingsRef.current);
      });
    }
  }, [getCurrentSettings]); // Only set up the getter once

  useEffect(() => {
    if (getApplySettingsChange) {
      getApplySettingsChange(handleSettingsChange);
    }
  }, [getApplySettingsChange, handleSettingsChange]);

  const handleSectionOrderChange = useCallback(
    (newOrder: string[]) => {
      handleSettingsChange({ sectionOrder: newOrder });
    },
    [handleSettingsChange],
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // View Mode - Only show preview
  if (isViewMode) {
    return (
      <Box sx={{ height: "100vh", backgroundColor: "#f5f5f5", p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <h2
            className="font-Saira font-[700] text-[19px]"
            style={{ color: themeColors?.primary }}
          >
            {formData?.name || t("businessTemplatePreview")}
          </h2>
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                color: themeColors?.primary,
                borderColor: themeColors?.primary,
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
              }}
            >
              {t("back")}
            </Button>
          )}
        </Box>

        <InvoicePreview
          templateType={templateType}
          settings={settings}
          theme={themeColors}
          formData={formData}
        />
      </Box>
    );
  }

  // Edit Mode - Show full customizer
  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
          width: "100%",
          background: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <h2
          className="font-Saira font-[700] text-[19px]"
          style={{ color: themeColors?.primary }}
        >
          {params?.id ? t("editBusinessTemplate") : t("addBusinessTemplate")}
        </h2>
        <Box className="flex gap-x-[10px] items-center flex-wrap">
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                color: themeColors?.primary,
                borderColor: themeColors?.primary,
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
              }}
            >
              {t("back")}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              const settingsToSave = { ...preFillLabels(settings) };
              // Remove descriptionDisplay if itemDisplayOptions exists
              if (settingsToSave.itemDisplayOptions) {
                delete settingsToSave.descriptionDisplay;
              }
              onSave?.(settingsToSave);
            }}
            disabled={IsBtnEnabled === false}
            sx={{
              background: themeColors?.primary,
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                background: themeColors?.primary,
                opacity: 0.9,
              },
            }}
          >
            {t("save")}
          </Button>
        </Box>
      </Box>
      <Grid
        container
        spacing={0}
        sx={{ height: "100%", backgroundColor: "#f5f5f5" }}
      >
        {/* Left Panel - Settings */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            maxHeight: "85vh",
            overflow: "auto",
            backgroundColor: "#fff",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ p: 2 }}>
            {formFields}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* General Settings */}
              <Box>
                <h3
                  className="font-Saira font-[600] text-[14px] mb-3 mt-2"
                  style={{ color: themeColors?.primary }}
                >
                  {t("generalSettings")}
                </h3>
                <GeneralSettingsPanel
                  settings={settings}
                  onChange={handleSettingsChange}
                  templateType={templateType}
                />
              </Box>

              {/* Logo Settings */}
              <Box>
                <h3
                  className="font-Saira font-[600] text-[14px] mb-3"
                  style={{ color: themeColors?.primary }}
                >
                  {t("logoSettings")}
                </h3>
                <LogoSettingsPanel
                  settings={settings}
                  onChange={handleSettingsChange}
                  templateType={templateType}
                  isThermalInvoice={
                    isInvoiceOrProformaTemplateType(templateType) &&
                    settings.printFormat === "thermal"
                  }
                />
              </Box>

              {/* Header Settings */}
              <Box>
                <h3
                  className="font-Saira font-[600] text-[14px] mb-3"
                  style={{ color: themeColors?.primary }}
                >
                  {t("headerSettings")}
                </h3>
                <HeaderSettingsPanel
                  settings={settings}
                  onChange={handleSettingsChange}
                  templateType={templateType}
                />
              </Box>

              {/* Vehicle Info Settings – hidden for thermal invoice receipts */}
              {templateType !== 5 &&
                !isProformaInvoiceTemplateType(templateType) &&
                !(
                  isInvoiceOrProformaTemplateType(templateType) &&
                  settings.printFormat === "thermal"
                ) && (
                  <Box>
                    <h3
                      className="font-Saira font-[600] text-[14px] mb-3"
                      style={{ color: themeColors?.primary }}
                    >
                      {t("vehicleInfo")}
                    </h3>
                    <VehicleInfoSettingsPanel
                      settings={settings}
                      onChange={handleSettingsChange}
                    />
                  </Box>
                )}

              {/* Table Settings - Hide for inspection only */}
              {templateType !== 5 && (
                <Box>
                  <h3
                    className="font-Saira font-[600] text-[14px] mb-3"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("table")}
                  </h3>
                  <TableSettingsPanel
                    settings={settings}
                    onChange={handleSettingsChange}
                  />
                </Box>
              )}

              {/* Inspection Settings - Only for inspection type */}
              {templateType === 5 && (
                <Box>
                  <h3
                    className="font-Saira font-[600] text-[14px] mb-3"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("inspectionSettings")}
                  </h3>
                  <InspectionSettingsPanel
                    settings={settings}
                    onChange={handleSettingsChange}
                  />
                </Box>
              )}

              {/* Summary & Footer Settings */}
              <Box>
                <h3
                  className="font-Saira font-[600] text-[14px] mb-3"
                  style={{ color: themeColors?.primary }}
                >
                  {templateType === 5
                    ? t("footer")
                    : `${t("summary")} & ${t("footer")}`}
                </h3>
                <SummaryFooterSettingsPanel
                  settings={settings}
                  onChange={handleSettingsChange}
                  templateType={templateType}
                />
              </Box>

              {/* Layout (section order incl. QR block on thermal invoices) */}
              <Box>
                <h3
                  className="font-Saira font-[600] text-[14px] mb-3"
                  style={{ color: themeColors?.primary }}
                >
                  {t("layout")}
                </h3>
                <LayoutCustomizer
                  settings={settings}
                  onChange={handleSectionOrderChange}
                  templateType={templateType}
                  isThermalInvoice={
                    isInvoiceOrProformaTemplateType(templateType) &&
                    settings.printFormat === "thermal"
                  }
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Right Panel - Preview */}
        <Grid
          item
          xs={12}
          md={8}
          sx={{
            maxHeight: "85vh",
            overflow: "auto",
            backgroundColor: "#f5f5f5",
            p: 2,
          }}
        >
          <InvoicePreview
            settings={settings}
            theme={themeColors}
            formData={formData}
            templateType={templateType}
          />
        </Grid>
      </Grid>
    </DndProvider>
  );
};

export default InvoiceTemplateCustomizer;
